import qs from 'qs'
import pako from 'pako'
import minimatch from 'minimatch'
import md5 from 'crypto-js/md5'
import store from '@/store'
import { salt, channel, tokenName, cacheWhiteList, loadingText as loadingMsg } from '@/config'
import { ShopTokenStorage, UidStorage, SettingStorage } from '@/utils/storage'
import { HttpStatusCode } from '@/constants'
import { Loading } from 'element-ui'
import i18n from '@/locale'
import router from '@/router'

// 添加签名
export function handleMd5Sign(config) {
  let sign = ''
  if (config.method.toLocaleLowerCase() !== 'get' && config.data) {
    if (config.data !== null && typeof config.data === 'object') {
      config.data = JSON.stringify(config.data)
    }
    sign = config.data
  } else if (config.params !== null && typeof config.params === 'object') {
    sign = qs.stringify(config.params, { addQueryPrefix: true, skipNulls: true, encode: false })
  }
  //当sign是纯数字是需要转成字符串
  sign = !isNaN(sign) ? String(sign) : sign
  const match = sign?.match(/[a-zA-Z0-9\u4e00-\u9fa5]/g)
  sign = match ? match.join('') : ''
  sign = md5(config.url + sign + salt).toString()
  const timestamp = new Date().getTime()
  config.headers['x-timestamp'] = timestamp
  config.headers['x-sign'] = md5(sign + timestamp).toString()
  return config
}

// 添加请求头
export function addHeader(config) {
  const accessToken = store.getters['user/accessToken']
  if (accessToken) {
    config.headers[tokenName] = accessToken
  }
  const shopToken = store.getters['user/shopToken'] || ShopTokenStorage.get()
  if (shopToken) {
    config.headers['X-SHOP-TOKEN'] = shopToken
  }
  const belongId = store.getters['user/belongId']
  if (belongId) {
    config.headers[`x-csp-belongId`] = belongId
  }
  const lang = store.getters['sys/lang']
  if (lang) {
    config.headers[`Accept-Language`] = {
      'zh-CN': 'zh-CN',
      'en-US': 'en-US',
      'th-TH': 'th-TH',
      id: 'id-ID',
    }[lang]
  }
  const belongCode = store.getters['user/belongCode']
  if (belongCode) {
    config.headers[`x-partner-code`] = belongCode
  }
  config.headers['x-ext-channel'] = channel
  config.headers['uuid'] = UidStorage.get()
  return config
}

// 压缩大数据请求体
export function gzipRequestBody(config) {
  try {
    const gzipConfig = store.getters['sys/gzipConfig']
    // 判断当前请求url是否需要压缩
    if (gzipConfig && gzipConfig?.matchUris.some((rule) => minimatch(config.url, rule))) {
      // 并且请求体大小是否超过 50kb(后台默认给的值)
      if (Buffer.from(config.data, 'utf-8').byteLength > 1024 * gzipConfig.thresholdKbSize) {
        config.headers['Content-Encoding'] = 'gzip'
        const bytes = pako.gzip(config.data)
        config.data = bytes
      }
    }
  } catch (error) {
    console.warn('压缩请求体失败')
  }
  return config
}

// 缓存接口请求数据
export async function addCache(config) {
  if (cacheWhiteList?.includes(config.url)) {
    // TODO 判断当前缓存是否过期，如果过期需要重新调用接口更新缓存
    const data = await LocalForage.getItem(config.url)
    if (data) {
      config.adapter = function (c) {
        return new Promise((resolve) => {
          resolve({
            config: c,
            data: {
              code: HttpStatusCode.CACHE_SUCCESS,
              data,
              msg: 'from cache',
            },
          })
        })
      }
    }
  }
  return config
}

/**
 * 收银扫码添加商品 - 从本地数据库中获取数据
 * @param {*} config
 * @returns
 */
export async function cashierProductScanByDB(config) {
  // 收银页面 & 快速添加商品接口 & 只搜条码 & 非弹窗搜索

  if (
    router.currentRoute.name === 'Cashier' &&
    config.url === '/mg/daohang-product-api/goodsCache/listFast' &&
    SettingStorage.get()?.ifCacheTopSku
  ) {
    const payload = config.data

    if (payload?.uesBarcode && !payload?.searchKeyTwo?.trim()) {
      const data = await LocalForage.getItem(payload.searchKey)
      if (data) {
        config.adapter = function (c) {
          return new Promise((resolve) => {
            resolve({
              config: c,
              data: {
                code: HttpStatusCode.CACHE_SUCCESS,
                data,
                msg: 'from local DB',
              },
            })
          })
        }
      }
    }
  }

  return config
}

export async function cashierPromoHitByDB(config) {
  // 收银页面/结算页面 & 单品促销命中/整单促销命中接口
  const singlePromoUrl = '/mg/daohang-promotion-api/promotion/isPromotionHitted'
  const wholePromoUrl = '/mg/daohang-promotion-api/promotion/isPromotionBillHit'

  const cashierRouteName = 'Cashier'
  const payRouteName = 'payPanel'

  if (
    [cashierRouteName, payRouteName].includes(router.currentRoute.name) &&
    [singlePromoUrl, wholePromoUrl].includes(config.url) &&
    SettingStorage.get()?.ifCacheTopSku
  ) {
    // 单品促销/整单促销对应 store 中的 action
    const urlCorrToAction = {}
    urlCorrToAction[singlePromoUrl] = 'localDB/singleProductPromoHit'
    urlCorrToAction[wholePromoUrl] = 'localDB/wholeOrderPromoHit'
    const data = await store.dispatch(urlCorrToAction[config.url], config.data)
    console.log('促销命中结果：', data)

    config.adapter = function (c) {
      return new Promise((resolve) => {
        resolve({
          config: c,
          data: {
            code: HttpStatusCode.CACHE_SUCCESS,
            data,
            msg: 'from local DB',
          },
        })
      })
    }
  }

  return config
}

// 显示全局loading
export function loading(config) {
  const { loading, loadingText } = config
  if (loading && !config.loadingInstance) {
    config.loadingInstance = Loading.service({ fullscreen: true, text: loadingText || i18n.t(loadingMsg) })
  }
  return config
}
//购物车促销请求唯一标识
export function addPromotionUid(config) {
  const usePromotionUid = config?.usePromotionUid
  if (usePromotionUid) {
    const promotionUid = new Date().getTime() + Math.random(1000, 9999)
    config.headers['x-promotion-uid'] = promotionUid
    store.dispatch('promotionReqQueue/actAddRequest', promotionUid)
  }

  return config
}

//设置外卖平台
export function addTakeawayPlatform(config) {
  const platformTypeUrl = [
    '/mg/mg-waimai-api/config/configSetting',
    '/mg/mg-waimai-api/takeoutOrder/page',
    '/mg/mg-waimai-api/platformSku/page',
    '/mg/mg-waimai-api/takeoutOrder/sum',
  ]
  if (platformTypeUrl.includes(config.url) || config.url.includes('/mg/daohang-product-api/productRelation')) {
    const methed = config.method.toLocaleLowerCase()
    const platformType = store.getters['takeaway/platform']
    if (methed === 'get') config.params = { ...config.params, platformType }
    else if (['post', 'put'].includes(methed)) {
      if (config.data?.constructor?.name === 'FormData') config.data.append('platformType', platformType)
      else config.data = { ...config.data, platformType }
    }
  }
  return config
}
